import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Building2, Phone } from 'lucide-react';
import ContactPopup from './ContactPopup';
import Logo from './Logo';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const location = useLocation();
  const isAboutPage = location.pathname === '/about';

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <header
      className={`fixed w-full z-[100] transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md py-2' : 'bg-transparent py-4'
      }`}
    >
      <div className="container mx-auto px-4 flex justify-between items-center">
        <Logo color={isScrolled ? 'dark' : 'light'} darkMode={isScrolled} />

        {/* Contact info */}
        <div className="hidden md:flex items-center space-x-4 text-sm">
          <a 
            href="tel:+448085310127" 
            className={`flex items-center gap-2 ${
              isScrolled ? 'text-primary-800' : 'text-white'
            }`}
          >
            <Phone size={16} />
            <span>+44 808 531 0127</span>
          </a>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center">
          <ul className={`flex space-x-8 ${
            isScrolled ? 'text-gray-800' : 'text-white'
          }`}>
            <li>
              <a href="/" className="flex items-center hover:text-accent-400 transition-colors">
                Home
              </a>
            </li>
            <li>
              <a href="/partners" className="flex items-center hover:text-accent-400 transition-colors">
                Partners
              </a>
            </li>
            <li>
              <a 
                href="/about" 
                className="hover:text-accent-400 transition-colors"
              >
                About
              </a>
            </li>
            <li>
              <a 
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setIsContactOpen(true);
                }}
                className={`px-6 py-2 rounded-md transition-colors ${
                  isScrolled
                    ? 'bg-primary-800 text-white hover:bg-primary-700'
                    : 'bg-white text-primary-800 hover:bg-gray-100'
                }`}
              >
                Contact Us
              </a>
            </li>
          </ul>
        </nav>

        {/* Mobile menu button */}
        <button
          className="md:hidden text-gray-800"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? (
            <X size={24} className={isScrolled ? 'text-gray-800' : 'text-white'} />
          ) : (
            <Menu size={24} className={isScrolled ? 'text-gray-800' : 'text-white'} />
          )}
        </button>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden bg-white shadow-lg animate-slide-down">
          <ul className="py-4 px-6 space-y-4">
            <li>
              <a
                href="/"
                className="block py-2 text-gray-800 hover:text-primary-700"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </a>
            </li>            
            <li>
              <a
                href="/partners"
                className="block py-2 text-gray-800 hover:text-primary-700"
                onClick={() => setIsMenuOpen(false)}
              >
                Partners
              </a>
            </li>
            <li>
              <a
                href="/about"
                className="block py-2 text-gray-800 hover:text-primary-700"
                onClick={() => setIsMenuOpen(false)}
              >
                About
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setIsContactOpen(true);
                  setIsMenuOpen(false);
                }}
                className="block py-2 px-4 bg-primary-800 text-white rounded-md text-center hover:bg-primary-700"
              >
                Contact Us
              </a>
            </li>
            <li className="pt-2 border-t border-gray-200">
              <a 
                href="tel:+448085310127" 
                className="flex items-center gap-2 text-primary-800"
              >
                <Phone size={16} />
                <span>+44 808 531 0127</span>
              </a>
            </li>
          </ul>
        </div>
      )}
      <ContactPopup isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} />
    </header>
  );
};

export default Header;